/** Shared helpers for Studio / production image nodes and durable uploads. */

export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const ACCEPTED_ARCHIVE_TYPES = ['application/zip', 'application/x-zip-compressed'];
export const ACCEPTED_UPLOAD_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.zip'];
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_ARCHIVE_BYTES = 250 * 1024 * 1024;

export function classifyUploadFile(file) {
  if (!file) return { kind: null, error: 'Choose a file.' };
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  const isZip =
    name.endsWith('.zip') || ACCEPTED_ARCHIVE_TYPES.includes(type);
  const isImage =
    ACCEPTED_IMAGE_TYPES.includes(type) ||
    /\.(png|jpe?g|webp)$/.test(name);
  if (isZip && isImage) {
    return { kind: null, error: 'Could not determine a supported file type.' };
  }
  if (isImage) {
    if (file.size <= 0 || file.size > MAX_IMAGE_BYTES) {
      return {
        kind: null,
        error: `Images must be under ${Math.round(MAX_IMAGE_BYTES / (1024 * 1024))} MB.`,
      };
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(type) && !/\.(png|jpe?g|webp)$/.test(name)) {
      return { kind: null, error: 'Choose a PNG, JPEG, or WebP image.' };
    }
    // Browser MIME may be empty; extension already passed. Reject known non-accepted image MIME.
    if (type.startsWith('image/') && !ACCEPTED_IMAGE_TYPES.includes(type)) {
      return { kind: null, error: 'Choose a PNG, JPEG, or WebP image.' };
    }
    return { kind: 'reference', error: '' };
  }
  if (isZip) {
    if (file.size <= 0 || file.size > MAX_ARCHIVE_BYTES) {
      return {
        kind: null,
        error: `ZIP archives must be under ${Math.round(MAX_ARCHIVE_BYTES / (1024 * 1024))} MB.`,
      };
    }
    return { kind: 'archive', error: '' };
  }
  return {
    kind: null,
    error: 'Unsupported file type. Upload a PNG, JPEG, WebP image, or a ZIP project archive.',
  };
}

export function validateImageFile(file) {
  if (!file) return 'Choose an image file.';
  const type = String(file.type || '').toLowerCase();
  if (type.startsWith('image/') && !ACCEPTED_IMAGE_TYPES.includes(type)) {
    return 'Choose a PNG, JPEG, or WebP image.';
  }
  const classified = classifyUploadFile(file);
  if (classified.kind === 'reference') return '';
  if (classified.error) return classified.error;
  return 'Choose a PNG, JPEG, or WebP image.';
}

export function promptExcerpt(prompt, max = 42) {
  const text = String(prompt || '').trim().replace(/\s+/g, ' ');
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export function imageNodePrompt(node) {
  if (!node) return '';
  return String(node.prompt ?? node.text ?? node.note ?? '');
}

export async function sha256Hex(buffer) {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function readImageDimensions(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

/**
 * Upload through initiate → PUT → confirm. Reports progress 0–100.
 * Pass `signal` (AbortSignal) to cancel the PUT; cancelled uploads do not confirm.
 */
export async function uploadProjectImage(
  bakingApi,
  projectId,
  file,
  { onProgress, signal, folderId, clientModifiedAt } = {}
) {
  const error = validateImageFile(file);
  if (error) throw Object.assign(new Error(error), { code: 'invalid_image' });
  if (signal?.aborted) {
    throw Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' });
  }

  const buffer = await file.arrayBuffer();
  const content_sha256 = await sha256Hex(buffer);
  const body = {
    original_filename: file.name,
    byte_size: file.size,
    content_sha256,
    content_type: file.type || 'image/png',
  };
  if (folderId !== undefined) body.folder_id = folderId;
  if (clientModifiedAt) body.client_modified_at = clientModifiedAt;
  const initiated = await bakingApi.initiateProjectImageUpload(projectId, body);
  const upload = initiated.upload || {};
  const imageId = initiated.image?.id;
  if (!upload.url || !imageId) {
    throw Object.assign(new Error('Upload authorization was incomplete.'), {
      code: 'upload_auth_failed',
    });
  }

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const onAbort = () => {
      xhr.abort();
      reject(Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' }));
    };
    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
    xhr.open((upload.method || 'PUT').toUpperCase(), upload.url);
    const headers = upload.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value != null) xhr.setRequestHeader(key, String(value));
    });
    if (!headers['Content-Type'] && !headers['content-type']) {
      xhr.setRequestHeader('Content-Type', file.type);
    }
    if (!headers['x-bakeonator-content-sha256'] && !headers['X-Bakeonator-Content-Sha256']) {
      xhr.setRequestHeader('x-bakeonator-content-sha256', content_sha256);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      signal?.removeEventListener('abort', onAbort);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else {
        reject(
          Object.assign(new Error(`Upload failed (${xhr.status}).`), {
            code: 'upload_failed',
            status: xhr.status,
          })
        );
      }
    };
    xhr.onerror = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(Object.assign(new Error('Network error during upload.'), { code: 'upload_failed' }));
    };
    xhr.onabort = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' }));
    };
    xhr.send(buffer);
  });

  if (signal?.aborted) {
    throw Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' });
  }

  const confirmed = await bakingApi.confirmProjectImageUpload(projectId, imageId);
  if (onProgress) onProgress(100);
  return confirmed.image;
}

/**
 * Replace an existing ready image: initiate replace → PUT → confirm.
 * Keeps the same image id; swaps storage after verification.
 */
export async function replaceProjectImage(
  bakingApi,
  projectId,
  imageId,
  file,
  { onProgress, signal, expectedUpdatedAt } = {}
) {
  const error = validateImageFile(file);
  if (error) throw Object.assign(new Error(error), { code: 'invalid_image' });
  if (signal?.aborted) {
    throw Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' });
  }

  const buffer = await file.arrayBuffer();
  const content_sha256 = await sha256Hex(buffer);
  const initiated = await bakingApi.initiateProjectImageReplace(projectId, imageId, {
    original_filename: file.name,
    byte_size: file.size,
    content_sha256,
    content_type: file.type,
    expected_updated_at: expectedUpdatedAt || undefined,
  });
  const upload = initiated.upload || {};
  if (!upload.url) {
    throw Object.assign(new Error('Upload authorization was incomplete.'), {
      code: 'upload_auth_failed',
    });
  }

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const onAbort = () => {
      xhr.abort();
      reject(Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' }));
    };
    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
    xhr.open((upload.method || 'PUT').toUpperCase(), upload.url);
    const headers = upload.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value != null) xhr.setRequestHeader(key, String(value));
    });
    if (!headers['Content-Type'] && !headers['content-type']) {
      xhr.setRequestHeader('Content-Type', file.type);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      signal?.removeEventListener('abort', onAbort);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else {
        reject(
          Object.assign(new Error(`Upload failed (${xhr.status}).`), {
            code: 'upload_failed',
            status: xhr.status,
          })
        );
      }
    };
    xhr.onerror = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(Object.assign(new Error('Network error during upload.'), { code: 'upload_failed' }));
    };
    xhr.onabort = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' }));
    };
    xhr.send(buffer);
  });

  if (signal?.aborted) {
    throw Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' });
  }

  const confirmed = await bakingApi.confirmProjectImageReplace(projectId, imageId, {
    expected_updated_at: initiated.image?.updated_at || expectedUpdatedAt || undefined,
  });
  if (onProgress) onProgress(100);
  return confirmed.image;
}

/**
 * Upload a ZIP archive as a new immutable project revision.
 */
export async function uploadProjectArchive(
  bakingApi,
  projectId,
  file,
  { onProgress, signal, folderId } = {}
) {
  const classified = classifyUploadFile(file);
  if (classified.kind !== 'archive') {
    throw Object.assign(new Error(classified.error || 'Choose a ZIP archive.'), {
      code: 'invalid_archive',
    });
  }
  if (signal?.aborted) {
    throw Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' });
  }

  const buffer = await file.arrayBuffer();
  const content_sha256 = await sha256Hex(buffer);
  const body = {
    original_filename: file.name,
    byte_size: file.size,
    content_sha256,
    content_type: file.type || 'application/zip',
  };
  if (folderId !== undefined) body.folder_id = folderId;
  const idempotencyKey =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `upload-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const initiated = await bakingApi.initiateRevisionUpload(projectId, body, idempotencyKey);
  const revisionId = initiated.revision_id || initiated.revision?.id;
  const uploadUrl = initiated.upload_url || initiated.upload?.url;
  const uploadMethod = initiated.upload_method || initiated.upload?.method || 'PUT';
  const headers = initiated.required_headers || initiated.upload?.headers || {};
  if (!uploadUrl || !revisionId) {
    throw Object.assign(new Error('Upload authorization was incomplete.'), {
      code: 'upload_auth_failed',
    });
  }

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const onAbort = () => {
      xhr.abort();
      reject(Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' }));
    };
    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
    xhr.open(String(uploadMethod).toUpperCase(), uploadUrl);
    Object.entries(headers).forEach(([key, value]) => {
      if (value != null) xhr.setRequestHeader(key, String(value));
    });
    if (!headers['Content-Type'] && !headers['content-type']) {
      xhr.setRequestHeader('Content-Type', file.type || 'application/zip');
    }
    if (!headers['x-bakeonator-content-sha256'] && !headers['X-Bakeonator-Content-Sha256']) {
      xhr.setRequestHeader('x-bakeonator-content-sha256', content_sha256);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      signal?.removeEventListener('abort', onAbort);
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else {
        reject(
          Object.assign(new Error(`Upload failed (${xhr.status}).`), {
            code: 'upload_failed',
            status: xhr.status,
          })
        );
      }
    };
    xhr.onerror = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(Object.assign(new Error('Network error during upload.'), { code: 'upload_failed' }));
    };
    xhr.onabort = () => {
      signal?.removeEventListener('abort', onAbort);
      reject(Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' }));
    };
    xhr.send(buffer);
  });

  if (signal?.aborted) {
    throw Object.assign(new Error('Upload cancelled.'), { code: 'upload_cancelled' });
  }

  const confirmed = await bakingApi.confirmRevisionUpload(revisionId, {
    byte_size: file.size,
    content_sha256,
  });
  if (onProgress) onProgress(100);
  return confirmed.revision || confirmed;
}

export async function uploadProjectFile(
  bakingApi,
  projectId,
  file,
  options = {}
) {
  const classified = classifyUploadFile(file);
  if (classified.kind === 'reference') {
    return {
      kind: 'reference',
      result: await uploadProjectImage(bakingApi, projectId, file, options),
    };
  }
  if (classified.kind === 'archive') {
    return {
      kind: 'archive',
      result: await uploadProjectArchive(bakingApi, projectId, file, options),
    };
  }
  throw Object.assign(new Error(classified.error || 'Unsupported file type.'), {
    code: 'unsupported_type',
  });
}

export function formatBytes(n) {
  const value = Number(n) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
